import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home } from 'lucide-react';
import { publicAPI, stableAPI } from '@/services/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from "@/contexts/AuthContext";
import { handleSubmit } from "@/utils/submitUtils";
import CostSummary from "@/components/CostSummary";
import LocationEditor from "@/components/LocationEditor";
import ImageUpload from "@/components/ImageUpload";
import FeatureAdToggle from "@/components/FeatureAdToggle";
import SubmitButton from "@/components/SubmitButton";
import BackLink from "@/components/BackLink";
import WaiverCheckBox from "@/components/WaiverCheckBox";
import Section from "@/components/Section";
import { computeCosts, defaultPricing } from "@/utils/costUtil";

const BOARDING_TYPES = ['full_board', 'part_board', 'diy', 'retirement', 'pasture', 'short_stay'];
const AMENITIES = [
	'indoor_arena', 'outdoor_arena', 'round_pen', 'horse_walker', 'paddocks', 'trails_access',
	'wash_bay', 'tack_room', 'supervision_24_7', 'farrier_scheduled', 'vet_on_site',
	'feed_included', 'hay_included', 'bedding_included', 'individual_turnout',
];
const PRICING_UNITS = ['night', 'week', 'month'];

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function CreateStableAdPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const isClone = location.pathname.includes('clone');
	const { id: editId } = useParams();
	const { user } = useAuth();
	const [images, setImages] = useState([]);
	const [pricing, setPricing] = useState(defaultPricing());
	const [waivedWithdrawalRight, setWaivedWithdrawalRight] = useState(false);
	const [form, setForm] = useState({
		title: '',
		description: '',
		price: '',
		pricing_unit: 'month',
		boarding_types: [],
		amenities: [],
		total_boxes: '',
		available_boxes: '',
		company_name: '',
		website: '',
		contact_phone: '',
		location_name: '',
		location_lat: null,
		location_lng: null,
		is_featured: false,
		featured_paid: false,
		hidden_images: []
	});
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		publicAPI.settings()
			.then(r => setPricing(
				prev => ({ ...prev, ...r.data })))
			.catch(() => {});
		if (editId) {
			stableAPI.get(editId).then(r => {
				const ad = r.data;
				setForm({
					title: ad.title || '',
					description: ad.description || '',
					price: ad.price || '',
					pricing_unit: ad.pricing_unit || 'month',
					boarding_types: ad.boarding_types || [],
					amenities: ad.amenities || [],
					total_boxes: ad.total_boxes || '',
					available_boxes: ad.available_boxes || '',
					company_name: ad.company_name || '',
					website: ad.website || '',
					contact_phone: ad.contact_phone || '',
					location_lat: ad.location_lat || '',
					location_lng: ad.location_lng || '',
					location_name: ad.location_name || '',
					is_featured: ad.is_featured || false,
					featured_paid: ad.featured_paid || false,
					hidden_images: ad.hidden_images || [],
					included_images: ad.included_images || pricing.free_images_limit || 5,
				});

				// Load existing images as thumbnails
				const existingImages = (ad.images || []).map((url, i) => ({
					url,
					preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
					thumbnail: ad.thumbnails?.[i] ? (ad.thumbnails[i].startsWith('/') ? `${ API_BASE }${ ad.thumbnails[i] }` : ad.thumbnails[i]) : null,
					enabled: !(ad.hidden_images || []).includes(url),
					isExisting: true
				}));
				// need to remove images in case of cloning as I don't have the objects to upload
				setImages(isClone ? [] : existingImages);

			}).catch((e) => {
				console.log(e)
				navigate('/dashboard')
			});
		}
	}, [user, editId, navigate]);

	const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

	const toggleIn = (key, value) => {
		setForm((p) => ({
			...p,
			[key]: p[key].includes(value) ? p[key].filter((x) => x !== value) : [...p[key], value],
		}));
	};

	// --- Image handlers ---
	const handleImages = (e) => {
		const files = Array.from(e.target.files);
		const newImgs = files.map(f => ({ file: f, preview: URL.createObjectURL(f), enabled: true, isExisting: false }));
		setImages(prev => [...prev, ...newImgs]);
	};
	const removeImage = (i) => {
		if (!images[i].isExisting) URL.revokeObjectURL(images[i].preview);
		setImages(prev => prev.filter((_, idx) => idx !== i));
	};
	const toggleImage = (i) => setImages(prev => prev.map((img, idx) =>
		idx === i ? { ...img, enabled: !img.enabled } : img));

	const submit = async (e) => {
		e.preventDefault();
		if (!form.title.trim() || !form.description.trim() || !form.price) {
			toast.error(t('common.fill_required'));
			return;
		}

		const cost = computeCosts(images, [], pricing, form.is_featured, form.featured_paid, form.included_images,
			form.included_videos);

		const newImages = cost.enabledImages.filter(i => !i.isExisting);
		const hiddenImgs = images.filter(i => i.isExisting && !i.enabled).map(i => i.url);

		const payload = {
			...form,
			price: parseFloat(form.price),
			total_boxes: form.total_boxes ? parseInt(form.total_boxes, 10) : null,
			available_boxes: form.available_boxes ? parseInt(form.available_boxes, 10) : null,
			location_lat: form.location_lat ? parseFloat(form.location_lat) : null,
			location_lng: form.location_lng ? parseFloat(form.location_lng) : null,
			school_horses_count: form.school_horses_count ? parseInt(form.school_horses_count, 10) : null,
			included_images: Math.max(pricing.free_images_limit, cost.enabledImages.length),
			hidden_images: hiddenImgs,
		};

		await handleSubmit(payload, stableAPI, isClone ? null : editId, 'stable', cost,
			newImages,[], form.is_featured, form.featured_paid, setSaving, t, navigate);
	};

	return (
		<div className="pt-20 md:pt-24 min-h-screen bg-[#F9F7F2]" data-testid="create-stable-page">
			<div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

				<BackLink title={form.title} uri="/stables" textKey='nav.back_to_results' />

				<div className="flex items-center gap-3 mb-2">
					<Home className="w-6 h-6 text-[#8B4513]" />
					<h1 className="text-3xl font-medium text-[#1A3C34] font-['Playfair_Display']">
						{ isClone ? t('stable.create_title') : editId ? t('stable.edit_title') : t('stable.create_title')}
					</h1>
				</div>
				<p className="text-sm text-[#666055] mb-6">{t('stable.create_subtitle')}</p>

				<form onSubmit={submit} className="space-y-6"	data-testid="stable-form">
					<Section title={t('horse.basic_info')}>
						<div className="mb-4">
							<Label>{t('horse.title_label')} *</Label>
							<Input value={form.title} onChange={(e) => set('title', e.target.value)} required maxLength={120}
										 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" data-testid="stable-title" />
						</div>

						<div className="mb-4">
							<Label>{t('horse.description')} *</Label>
							<Textarea value={form.description} onChange={(e) => set('description', e.target.value)}
												required rows={5} maxLength={4000}
												className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm text-sm mt-1"
												data-testid="stable-description" />
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<div>
								<Label>{t('horse.price')} (€) *</Label>
								<Input type="number" min={0} step="any" value={form.price}
											 onChange={(e) => set('price', e.target.value)} required
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
											 data-testid="stable-price" />
							</div>
							<div>
								<Label>{t('transport.pricing_unit')}</Label>
								<Select value={form.pricing_unit} onValueChange={(v) => set('pricing_unit', v)}>
									<SelectTrigger className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>{PRICING_UNITS.map(u =>
										<SelectItem key={u} value={u}>{t(`stable.unit.${u}`)}</SelectItem>)}
									</SelectContent>
								</Select>
							</div>
						</div>
					</Section>

					<Section title={t('horse.details')}>
						<div className="mb-4">
							<Label className="block mb-2">{t('stable.boarding_types')}</Label>
							<div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
								{BOARDING_TYPES.map((b) => (
									<label key={b} className="flex items-center gap-2 cursor-pointer text-sm text-[#3a3a3a] select-none">
										<Checkbox checked={form.boarding_types.includes(b)}
															onCheckedChange={() => toggleIn('boarding_types', b)}
															data-testid={`stable-boarding-${b}`} />
										{t(`stable.boarding_opt.${b}`)}
									</label>
								))}
							</div>
						</div>

						<div className="mb-4">
							<Label className="block mb-2">{t('stable.amenities')}</Label>
							<div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
								{AMENITIES.map((a) => (
									<label key={a} className="flex items-center gap-2 cursor-pointer text-sm text-[#3a3a3a] select-none">
										<Checkbox checked={form.amenities.includes(a)}
															onCheckedChange={() => toggleIn('amenities', a)}
															data-testid={`stable-amenity-${a}`} />
										{t(`stable.amenity_opt.${a}`)}
									</label>
								))}
							</div>
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
							<div>
								<Label>{t('stable.total_boxes')}</Label>
								<Input type="number" min={0} value={form.total_boxes}
											 onChange={(e) => set('total_boxes', e.target.value)}
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
											 data-testid="stable-total-boxes" />
							</div>
							<div>
								<Label>{t('stable.available_boxes')}</Label>
								<Input type="number" min={0} value={form.available_boxes}
											 onChange={(e) => set('available_boxes', e.target.value)}
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1"
											 data-testid="stable-available-boxes" />
							</div>
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
							<div>
								<Label>{t('transport.company_name')}</Label>
								<Input value={form.company_name}
											 onChange={(e) => set('company_name', e.target.value)}
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
							</div>
							<div>
								<Label>{t('school.website')}</Label>
								<Input value={form.website}
											 onChange={(e) => set('website', e.target.value)}
											 placeholder="https://..."
											 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
							</div>
						</div>

						<div className="mb-4">
							<Label>{t('horse.contact_phone')}</Label>
							<Input value={form.contact_phone}
										 onChange={(e) => set('contact_phone', e.target.value)}
										 className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11 mt-1" />
						</div>
					</Section>

					{/* Location */}
					<LocationEditor
						location_name={ form.location_name }
						location_lat={ form.location_lat }
						location_lng={ form.location_lng }
						update={ set }/>

					{/* Images */}
					<ImageUpload images={ images }
											 pricing={ pricing }
											 handleImages={ handleImages }
											 removeImage={ removeImage }
											 toggleImage={ toggleImage } />

					{/* Featured Ad */}
					<FeatureAdToggle is_featured={ form.is_featured } pricing={ pricing }  update={ set } />

					{/* Cost Summary */}
					<CostSummary
						images={ images }
						pricing={ pricing }
						includedImages={ form.included_images }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						t={ t }/>

					{/* only display if there is a cost */ }
					<WaiverCheckBox
						images={ images }
						pricing={ pricing }
						includedImages={ form.included_images }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						onClick={ setWaivedWithdrawalRight }
						state={ waivedWithdrawalRight }
						t={ t }/>

					<SubmitButton
						editId={ isClone ? null : editId }
						loading={ saving }
						label='stable.publish_btn'
						images={ images }
						pricing={ pricing }
						includedImages={ form.included_images }
						isFeatured={ form.is_featured }
						featuredPaid={ form.featured_paid }
						waivedWithdrawalRight={ waivedWithdrawalRight }
					/>

				</form>
			</div>
		</div>
	);
}